import { atom } from 'jotai';
import type { WritableAtom, PrimitiveAtom } from 'jotai';
import { atomWithLazy } from 'jotai/utils';
import { atomFamily, type AtomFamily } from 'jotai-family';
import * as Sentry from '@sentry/react';
import {
  getOrgPreferenceKey,
  IPreferencesV3DataModel,
  PreferenceKey,
  PreferenceScope,
} from '../data-models/preferences-v3.data-model';
import { deletePreferenceV3, updatePreferencesV3 } from '../services/queries/MaggiePreferencesQueries';
import { FDMap } from './data-structure/FDMap';

export class PreferencesServiceV3 {
  static #instance: PreferencesServiceV3 | undefined;
  #preferences: FDMap<PreferenceScope, FDMap<PreferenceKey, unknown>>;

  private constructor() {
    this.#preferences = new FDMap<PreferenceScope, FDMap<PreferenceKey, unknown>>([
      [PreferenceScope.USER, new FDMap<PreferenceKey, unknown>()],
      [PreferenceScope.ORGANIZATION, new FDMap<PreferenceKey, unknown>()],
    ]);
  }

  static initService(preferences: IPreferencesV3DataModel<unknown>[]) {
    if (!this.#instance) {
      this.#instance = new PreferencesServiceV3();
    }

    const prefsAsMap = preferences.reduce((res, preference) => {
      const { scope, preferenceKey, value } = preference;

      return res.setOrUpdate(scope, new FDMap([[preferenceKey as PreferenceKey, value]]), (existingPrefs) =>
        existingPrefs.set(preferenceKey as PreferenceKey, value)
      );
    }, new FDMap<PreferenceScope, FDMap<PreferenceKey, unknown>>());

    this.#instance.setAllPreferences(prefsAsMap);

    return this.#instance;
  }

  static get() {
    if (!this.#instance) {
      throw new Error('Preferences service not initialized');
    }

    return this.#instance;
  }

  static destroyService() {
    this.#instance = undefined;
  }

  setAllPreferences(preferences: FDMap<PreferenceScope, FDMap<PreferenceKey, unknown>>) {
    this.#preferences = preferences;
  }

  getAllPreferences(scope = PreferenceScope.USER) {
    return this.#preferences.get(scope);
  }

  getPreference<T>(preferenceKey: PreferenceKey, scope = PreferenceScope.USER): T | undefined {
    return this.#preferences.get(scope)?.get(preferenceKey) as T;
  }

  setPreference<T>(key: PreferenceKey, value: T, scope = PreferenceScope.USER) {
    this.#preferences.getOrDefault(scope, new FDMap()).set(key, value);

    return updatePreferencesV3({
      scope,
      preferenceKey: key,
      value,
    });
  }

  /**
   * Refresh the in-memory copy of a preference another endpoint has already
   * persisted, so later reads see the saved value without a second request.
   */
  cachePreference<T>(key: PreferenceKey, value: T, scope = PreferenceScope.USER) {
    this.#preferences.getOrDefault(scope, new FDMap()).set(key, value);
  }

  deletePreference(preference: Pick<IPreferencesV3DataModel<unknown>, 'scope' | 'preferenceKey'>) {
    if (!this.#preferences.get(preference.scope)?.get(preference.preferenceKey)) {
      Sentry.logger.warn('Preference not found in scope', {
        preferenceKey: preference.preferenceKey,
        scope: preference.scope,
      });
      return;
    }
    this.#preferences.get(preference.scope)?.delete(preference.preferenceKey);
    return deletePreferenceV3(preference);
  }
}

export type PreferenceRef<T> = Pick<IPreferencesV3DataModel<T>, 'preferenceKey' | 'scope'>;

export function preferenceAtom<T>(
  getterPrefRef: PreferenceRef<T> | PreferenceRef<T>[],
  setterPrefRef: PreferenceRef<T> | undefined,
  defaultValue: T
): ReturnType<typeof atom<T, [newValue: T], void>>;
export function preferenceAtom<T>(
  getterPrefRef: PreferenceRef<T> | PreferenceRef<T>[],
  setterPrefRef?: PreferenceRef<T>
): ReturnType<typeof atom<T | undefined, [newValue: T], void>>;
export function preferenceAtom<T>(
  getterPrefRef: PreferenceRef<T> | PreferenceRef<T>[],
  setterPrefRef?: PreferenceRef<T>,
  defaultValue?: T
) {
  const baseAtom = atomWithLazy(() => {
    let _getterPrefRef;
    if (Array.isArray(getterPrefRef)) {
      _getterPrefRef = getFirstKeyWithValue(getterPrefRef) ?? getterPrefRef[0];
    } else {
      _getterPrefRef = getterPrefRef;
    }

    return PreferencesServiceV3.get().getPreference(_getterPrefRef.preferenceKey, _getterPrefRef.scope) as
      T | undefined;
  });

  return atom(
    (get) => {
      return get(baseAtom) ?? defaultValue;
    },
    (_get, set, newValue: T) => {
      let _setterPrefRef;

      if (Array.isArray(getterPrefRef)) {
        _setterPrefRef = setterPrefRef ?? getterPrefRef[0];
      } else {
        _setterPrefRef = setterPrefRef ?? getterPrefRef;
      }

      set(baseAtom, newValue);
      PreferencesServiceV3.get().setPreference(_setterPrefRef.preferenceKey, newValue, _setterPrefRef.scope);
    }
  );
}

function getFirstKeyWithValue<T>(preferenceRefs: PreferenceRef<T>[]) {
  return preferenceRefs.find(
    (pref) => PreferencesServiceV3.get().getPreference(pref.preferenceKey, pref.scope) !== undefined
  );
}

export interface CompanyOrDefaultPrefAtomsConfig<T> {
  preferenceKey: PreferenceKey;
  defaultValue: T;
  /** Must be an atom so activeAtom can reactively re-derive when the selection changes. */
  selectedCompanyIdAtom: PrimitiveAtom<number | null>;
}

export interface CompanyOrDefaultPrefAtoms<T> {
  orgDefaultAtom: WritableAtom<T, [T], void>;
  companyOverrideAtom: AtomFamily<number, WritableAtom<T, [T], void>>;
  activeAtom: WritableAtom<T, [T | null], void>;
}

export function createCompanyOrDefaultPrefAtoms<T>(
  config: CompanyOrDefaultPrefAtomsConfig<T>
): CompanyOrDefaultPrefAtoms<T> {
  const { preferenceKey, defaultValue, selectedCompanyIdAtom } = config;

  const orgDefaultAtom = preferenceAtom<T>(
    {
      preferenceKey: getOrgPreferenceKey(preferenceKey, null),
      scope: PreferenceScope.ORGANIZATION,
    },
    undefined,
    defaultValue
  );

  const companyOverrideAtom = atomFamily((companyId: number) => {
    return preferenceAtom<T>(
      [
        {
          preferenceKey: getOrgPreferenceKey(preferenceKey, companyId),
          scope: PreferenceScope.ORGANIZATION,
        },
        {
          preferenceKey: getOrgPreferenceKey(preferenceKey, null),
          scope: PreferenceScope.ORGANIZATION,
        },
      ],
      {
        preferenceKey: getOrgPreferenceKey(preferenceKey, companyId),
        scope: PreferenceScope.ORGANIZATION,
      },
      defaultValue
    );
  });

  // Internal signal to force activeAtom re-derivation after atomFamily.remove().
  // Avoids the fragile toggle-hack pattern (setting selectedCompanyIdAtom to null then back).
  const derivationVersion = atom(0);

  const activeAtom = atom(
    (get) => {
      get(derivationVersion);
      const companyId = get(selectedCompanyIdAtom);
      if (companyId !== null) {
        return get(companyOverrideAtom(companyId));
      }
      return get(orgDefaultAtom);
    },
    (get, set, newValue: T | null) => {
      const companyId = get(selectedCompanyIdAtom);
      if (newValue === null && companyId !== null) {
        const companyKey = getOrgPreferenceKey(preferenceKey, companyId);
        const hasOverride =
          PreferencesServiceV3.get().getPreference(companyKey, PreferenceScope.ORGANIZATION) !== undefined;
        if (hasOverride) {
          PreferencesServiceV3.get()
            .deletePreference({ preferenceKey: companyKey, scope: PreferenceScope.ORGANIZATION })
            ?.catch(Sentry.captureException);
        }
        companyOverrideAtom.remove(companyId);
        set(derivationVersion, get(derivationVersion) + 1);
      } else if (newValue !== null) {
        if (companyId !== null) {
          set(companyOverrideAtom(companyId), newValue);
        } else {
          set(orgDefaultAtom, newValue);
        }
      }
    }
  );

  return {
    orgDefaultAtom,
    companyOverrideAtom,
    activeAtom,
  };
}
